/**
 * Profile data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { parseISO } from "date-fns";
import { z } from "zod";

// Domain schema (camelCase)
const ProfileSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  userId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  profileImageSrc: z.string().nullable(),
  gender: z.string().nullable(),
  bio: z.string().nullable(),
  deletedAt: z.date().nullable(),
  createdBy: z.string().nullable(),
});

const CreateProfileRequestSchema = ProfileSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  userId: true,
  deletedAt: true,
  createdBy: true,
}).partial();

const UpdateProfileRequestSchema = ProfileSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  userId: true,
  deletedAt: true,
  createdBy: true,
}).partial();

// DTO schema (snake_case)
const ProfileDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  user_id: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  display_name: z.string().nullable(),
  profile_image_src: z.string().nullable(),
  gender: z.string().nullable(),
  bio: z.string().nullable(),
  deleted_at: z.string().nullable(),
  created_by: z.string().nullable(),
});

export const CreateProfileDtoSchema = ProfileDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  user_id: true,
  deleted_at: true,
  created_by: true,
}).partial();

export const UpdateProfileDtoSchema = ProfileDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  user_id: true,
  deleted_at: true,
  created_by: true,
}).partial();

// Infer DTO types
export type ProfileDto = z.infer<typeof ProfileDtoSchema>;
export type CreateProfileDto = z.infer<typeof CreateProfileDtoSchema>;
export type UpdateProfileDto = z.infer<typeof UpdateProfileDtoSchema>;

// Infer domain types
export type Profile = z.infer<typeof ProfileSchema>;
export type CreateProfileRequest = z.infer<typeof CreateProfileRequestSchema>;
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromProfileDtoToProfile(dto: ProfileDto): Profile {
  const profileDto = ProfileDtoSchema.parse(dto);

  return {
    id: profileDto.id,
    createdAt: parseISO(profileDto.created_at),
    modifiedAt: parseISO(profileDto.modified_at),
    userId: profileDto.user_id,
    firstName: profileDto.first_name,
    lastName: profileDto.last_name,
    displayName: profileDto.display_name,
    profileImageSrc: profileDto.profile_image_src,
    gender: profileDto.gender,
    bio: profileDto.bio,
    deletedAt: profileDto.deleted_at ? parseISO(profileDto.deleted_at) : null,
    createdBy: profileDto.created_by,
  };
}

export function fromCreateProfileRequestToCreateProfileDto(
  request: CreateProfileRequest
): CreateProfileDto {
  const createProfileRequest = CreateProfileRequestSchema.parse(request);

  return {
    first_name: createProfileRequest.firstName,
    last_name: createProfileRequest.lastName,
    display_name: createProfileRequest.displayName,
    profile_image_src: createProfileRequest.profileImageSrc,
    gender: createProfileRequest.gender,
    bio: createProfileRequest.bio,
  };
}

export function fromUpdateProfileRequestToUpdateProfileDto(
  request: UpdateProfileRequest
): UpdateProfileDto {
  const updateProfileRequest = UpdateProfileRequestSchema.parse(request);

  return {
    first_name: updateProfileRequest.firstName,
    last_name: updateProfileRequest.lastName,
    display_name: updateProfileRequest.displayName,
    profile_image_src: updateProfileRequest.profileImageSrc,
    gender: updateProfileRequest.gender,
    bio: updateProfileRequest.bio,
  };
}
